import React, { useEffect, useMemo, useState } from "react";
import { client, STATUS_META, fmtTimeAST } from "@/lib/api";
import { AlertTriangle, Users, Wrench, DollarSign } from "lucide-react";

export default function Dashboard() {
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [areas, setAreas] = useState([]);
  const [operations, setOperations] = useState(null);

  useEffect(() => {
    (async () => {
      const [j, t, c, s, a, report] = await Promise.all([
        client.get("/jobs"), client.get("/technicians"),
        client.get("/customers"), client.get("/sites"), client.get("/areas"),
        client.get("/reports/operations"),
      ]);
      setJobs(j.data); setTechs(t.data); setCustomers(c.data); setSites(s.data); setAreas(a.data);
      setOperations(report.data);
    })();
  }, []);

  const metrics = useMemo(() => {
    const CLOSED = ["complete", "tech_complete"];
    const completed = jobs.filter(j => CLOSED.includes(j.status));
    const active = jobs.filter(j => !CLOSED.includes(j.status));
    const breached = active.filter(j => j.status !== "on_hold" && !j.sla_excluded && new Date(j.sla_due_at) < new Date());
    const slaCompleted = completed.filter(j => !j.sla_excluded);
    const completedOnTime = slaCompleted.filter(j => j.completed_at && j.sla_due_at && new Date(j.completed_at) <= new Date(j.sla_due_at));
    const attainment = slaCompleted.length ? Math.round((completedOnTime.length / slaCompleted.length) * 100) : 0;

    // breach reasons: group by priority bucket and by "equipment down"
    const reasonCounts = { "Equipment Down": 0, "Awaiting Parts": 0, "Standard overrun": 0, "Low priority overrun": 0 };
    breached.forEach(j => {
      if (j.priority === "equipment_down") reasonCounts["Equipment Down"]++;
      else if (j.status === "awaiting_parts") reasonCounts["Awaiting Parts"]++;
      else if (j.priority === "standard") reasonCounts["Standard overrun"]++;
      else reasonCounts["Low priority overrun"]++;
    });
    const totalBreach = breached.length || 1;

    // tech utilization — real technicians only; the owner account holds the role for workspace
    // switching but is not a technician, so it never appears as a row.
    const util = techs.filter(t => !t.owner).map(t => {
      const mine = jobs.filter(j => j.assigned_tech_id === t.id);
      const mineComp = mine.filter(j => CLOSED.includes(j.status));
      const hours = mineComp.reduce((a, j) => a + (Number(j.labour_hours) || 0), 0);
      return {
        id: t.id, name: t.name,
        total: mine.length, active: mine.length - mineComp.length, completed: mineComp.length,
        hours, onTime: mineComp.filter(j => j.completed_at && new Date(j.completed_at) <= new Date(j.sla_due_at)).length,
      };
    }).sort((a, b) => b.hours - a.hours);

    // parts spend
    const partsBillable = completed.flatMap(j => (j.parts_used || []).filter(p => !p.covered));
    const partsCovered = completed.flatMap(j => (j.parts_used || []).filter(p => p.covered));
    const spend = partsBillable.reduce((a, p) => a + (Number(p.unit_price) || 0) * (Number(p.qty) || 1), 0);
    const saved = partsCovered.reduce((a, p) => a + (Number(p.unit_price) || 0) * (Number(p.qty) || 1), 0);

    // top customers by activity
    const byCust = {};
    jobs.forEach(j => { byCust[j.customer_id] = (byCust[j.customer_id] || 0) + 1; });
    const topCustomers = customers.map(c => ({ ...c, count: byCust[c.id] || 0 })).sort((a, b) => b.count - a.count).slice(0, 5);

    return {
      completed: completed.length, slaCompleted: slaCompleted.length, active: active.length, breached: breached.length,
      attainment, reasonCounts, totalBreach, util, spend, saved, topCustomers,
    };
  }, [jobs, techs, customers]);

  return (
    <div data-testid="dashboard-page">
      <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Operations</div>
      <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>SLA Dashboard</h1>
      <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>Attainment, breach reasons, tech utilisation — the numbers your next contract depends on.</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
        <SlaDonut attainment={metrics.attainment} completed={metrics.slaCompleted} />
        <Big icon={<AlertTriangle size={16} />} label="Breached (active)" value={metrics.breached} sub={`${metrics.active} active · held jobs excluded`} tint="#B42318" />
        <Big icon={<Wrench size={16} />} label="Labour completed" value={`${metrics.util.reduce((a, u) => a + u.hours, 0).toFixed(1)}h`} sub="across all techs" tint="#2563EB" />
        <Big icon={<DollarSign size={16} />} label="Parts spend / saved" value={`$${metrics.spend.toFixed(0)}`} sub={`$${metrics.saved.toFixed(0)} covered by warranty`} tint="#96650A" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-5 mt-8">
        {/* Breach reasons */}
        <section className="bf-card" style={{ padding: "20px" }} data-testid="breach-reasons">
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Why we breach</div>
          <h3 className="text-[15px] font-semibold mt-1">Breach reasons · active tickets</h3>
          {metrics.breached === 0 ? (
            <div className="mt-6 text-[13.5px]" style={{ color: "#136B36" }}>Clean board — no active breaches.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {Object.entries(metrics.reasonCounts).filter(([, v]) => v > 0).map(([k, v]) => (
                <div key={k}>
                  <div className="flex items-center justify-between text-[13.5px]">
                    <span>{k}</span>
                    <span className="mono" style={{ color: "#5B6472" }}>{v} · {Math.round((v / metrics.totalBreach) * 100)}%</span>
                  </div>
                  <div className="h-2 rounded-full mt-1.5 overflow-hidden" style={{ background: "#EFF1F4" }}>
                    <div className="h-full rounded-full"
                      style={{
                        width: `${Math.round((v / metrics.totalBreach) * 100)}%`,
                        background: k === "Equipment Down" ? "#B42318" : k === "Awaiting Parts" ? "#5B21B6" : k === "Standard overrun" ? "#96650A" : "#94A3B8",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Top customers */}
        <section className="bf-card" style={{ padding: "20px" }} data-testid="top-customers">
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Volume</div>
          <h3 className="text-[15px] font-semibold mt-1">Top customers · all time</h3>
          <ul className="mt-4 space-y-2.5">
            {metrics.topCustomers.map(c => (
              <li key={c.id || `cust-${c.name}`} className="flex items-center gap-3">
                <div className="bf-avatar mono" style={{ width: 28, height: 28, minWidth: 28 }}>{c.code?.replace("C-", "")}</div>
                <div className="flex-1 text-[13.5px]">{c.name}</div>
                <div className="mono text-[12px]" style={{ color: "#5B6472" }}>{c.count} ticket{c.count !== 1 ? "s" : ""}</div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Tech utilisation */}
      <section className="bg-white rounded-[14px] mt-8 overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }} data-testid="tech-utilisation">
        <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#EFF1F4" }}>
          <Users size={14} color="#2563EB" />
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Technician utilisation</div>
        </div>
        <table className="w-full text-[13.5px]">
          <thead>
            <tr style={{ background: "#F7F8FA" }}>
              <th className="th-condensed text-left px-5 py-2">Technician</th>
              <th className="th-condensed text-left px-3 py-2">Active</th>
              <th className="th-condensed text-left px-3 py-2">Completed</th>
              <th className="th-condensed text-left px-3 py-2">On-time %</th>
              <th className="th-condensed text-left px-3 py-2">Labour (h)</th>
              <th className="th-condensed text-left px-3 py-2">Load</th>
            </tr>
          </thead>
          <tbody>
            {metrics.util.map(u => {
              const maxH = Math.max(...metrics.util.map(x => x.hours), 1);
              const otPct = u.completed ? Math.round((u.onTime / u.completed) * 100) : 0;
              return (
                <tr key={u.id} className="border-t" style={{ borderColor: "#EFF1F4" }} data-testid={`util-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium">{u.name}</td>
                  <td className="px-3 py-3 mono">{u.active}</td>
                  <td className="px-3 py-3 mono">{u.completed}</td>
                  <td className="px-3 py-3 mono" style={{ color: otPct >= 80 ? "#136B36" : otPct >= 60 ? "#96650A" : "#B42318" }}>
                    {u.completed ? `${otPct}%` : "—"}
                  </td>
                  <td className="px-3 py-3 mono">{u.hours.toFixed(1)}</td>
                  <td className="px-3 py-3">
                    <div className="h-2 rounded-full" style={{ background: "#EFF1F4", width: 180 }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.round((u.hours / maxH) * 100)}%`, background: "#2563EB" }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {operations && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8" data-testid="operations-report-summary">
          <div className="bf-card" style={{ padding: "20px" }}>
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
              Holds by reason
            </div>
            <div className="mt-3 space-y-2 text-[13.5px]">
              {Object.entries(operations.holds_by_reason).map(([reason, count]) => (
                <div key={reason} className="flex justify-between" data-testid={`hold-reason-${reason}`}>
                  <span className="capitalize">{reason}</span>
                  <span className="mono">{count}</span>
                </div>
              ))}
              {!Object.keys(operations.holds_by_reason).length && <span style={{ color: "#8A93A1" }}>No holds recorded.</span>}
            </div>
          </div>
          <div className="bf-card" style={{ padding: "20px" }}>
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
              Repeat-failure equipment
            </div>
            <div className="mt-3 space-y-2 text-[13.5px]">
              {operations.repeat_failure_equipment.map(item => (
                <div key={item.equipment_id} className="flex justify-between" data-testid={`repeat-equipment-${item.equipment_id}`}>
                  <span className="mono">{item.equipment_id}</span>
                  <span>{item.count} jobs</span>
                </div>
              ))}
              {!operations.repeat_failure_equipment.length && <span style={{ color: "#8A93A1" }}>No equipment has three failures yet.</span>}
            </div>
          </div>
        </section>
      )}

      {operations && (
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8" data-testid="report-breakdowns">
          <ReportList title="Jobs by customer" items={operations.jobs_by_customer} />
          <ReportList title="Jobs by site" items={operations.jobs_by_site} />
          <ReportList
            title={`Parts used · ${operations.pm_overdue} PM overdue`}
            items={operations.parts_used.map(item => ({ name: item.name, count: item.quantity }))}
          />
        </section>
      )}
    </div>
  );
}

function Big({ icon, label, value, sub, tint }) {
  return (
    <div className="bf-card">
      <div className="flex items-center gap-1.5 font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        <span style={{ color: tint }}>{icon}</span>{label}
      </div>
      <div className="text-[32px] font-semibold mt-1.5 mono" style={{ color: "#12161B", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{value}</div>
      <div className="text-[12px] mt-1" style={{ color: "#5B6472" }}>{sub}</div>
    </div>
  );
}

function SlaDonut({ attainment, completed }) {
  return (
    <div
      className="bf-card flex items-center gap-3"
      data-testid="sla-completion-donut"
    >
      <div
        className="h-16 w-16 rounded-full flex items-center justify-center"
        style={{
          background: `conic-gradient(#2563EB ${attainment * 3.6}deg, #EAF0FE 0deg)`,
        }}
      >
        <div className="h-12 w-12 rounded-full bg-white flex flex-col items-center justify-center">
          <span className="mono text-[14px] font-semibold">{attainment}%</span>
          <span className="mono text-[8px]" style={{ color: "#5B6472" }}>{completed} done</span>
        </div>
      </div>
      <div>
        <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
          SLA completion
        </div>
        <div className="text-[12px] mt-1" style={{ color: "#5B6472" }}>Completed within the SLA due time</div>
      </div>
    </div>
  );
}

function ReportList({ title, items }) {
  return (
    <section className="bf-card" style={{ padding: "20px" }}>
      <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        {title}
      </div>
      <div className="mt-3 space-y-2 text-[13.5px]">
        {items.slice(0, 6).map((item, index) => (
          <div key={`${item.name}-${index}`} className="flex justify-between gap-3">
            <span className="truncate">{item.name}</span>
            <span className="mono">{item.count}</span>
          </div>
        ))}
        {!items.length && <span style={{ color: "#8A93A1" }}>No data yet.</span>}
      </div>
    </section>
  );
}
